// src/Dashboard/pages/teacher/ListingAdherents/components/ListingAdherentsBirthdaysCard.jsx

import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  FaBell,
  FaBirthdayCake,
  FaCalendarAlt,
  FaChevronDown,
  FaChevronUp,
  FaGift,
  FaSyncAlt,
  FaUserClock,
} from "react-icons/fa";
import { toast } from "react-toastify";

import { sendNotification } from "../../../../../services/notificationServices";
import {
  getUpcomingBirthdays,
  getUserIdByAdherentId,
} from "../../../../../services/adherentServices";

const API_BASE = (process.env.REACT_APP_API_URL || "").replace(/\/$/, "");

const pickUserId = (u) =>
  u?.UserAdherent?.user_id ?? u?.user_id ?? u?.id ?? null;

const getDefaultPhoto = (genre) =>
  genre === "Féminin" ? "/img/judokate_avatar.png" : "/img/judoka_avatar.png";

const toAbsolute = (url) => {
  if (!url) return "";

  const value = String(url).trim();

  if (
    /^https?:\/\//i.test(value) ||
    value.startsWith("data:") ||
    value.startsWith("blob:")
  ) {
    return value;
  }

  if (!value.startsWith("/uploads")) {
    return value;
  }

  return `${API_BASE}${value}`;
};

const getDisplayName = (adherent) => {
  const nom = String(adherent?.nom || "").trim().toUpperCase();
  const prenom = String(adherent?.prenom || "").trim();

  return `${nom} ${prenom}`.trim() || "Adhérent";
};

const getFirstName = (adherent) =>
  String(adherent?.prenom || "").trim() || "cet adhérent";

const startOfDay = (date) => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
};

const isLeapYear = (year) =>
  (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;

const getNextBirthdayOccurrence = (birthDate, fromDate = new Date()) => {
  if (!birthDate) return null;

  const birth = new Date(birthDate);
  if (Number.isNaN(birth.getTime())) return null;

  const from = startOfDay(fromDate);
  const year = from.getFullYear();

  let occurrence = new Date(year, birth.getMonth(), birth.getDate());

  if (birth.getMonth() === 1 && birth.getDate() === 29 && !isLeapYear(year)) {
    occurrence = new Date(year, 1, 28);
  }

  occurrence = startOfDay(occurrence);

  if (occurrence < from) {
    const nextYear = year + 1;

    occurrence = new Date(nextYear, birth.getMonth(), birth.getDate());

    if (
      birth.getMonth() === 1 &&
      birth.getDate() === 29 &&
      !isLeapYear(nextYear)
    ) {
      occurrence = new Date(nextYear, 1, 28);
    }

    occurrence = startOfDay(occurrence);
  }

  return occurrence;
};

const getDaysFromToday = (date) => {
  if (!date) return null;

  const msDay = 24 * 60 * 60 * 1000;

  return Math.round(
    (startOfDay(date).getTime() - startOfDay(new Date()).getTime()) / msDay
  );
};

const getAgeAtDate = (birthDate, targetDate = new Date()) => {
  if (!birthDate) return null;

  const birth = new Date(birthDate);
  const target = new Date(targetDate);

  if (Number.isNaN(birth.getTime()) || Number.isNaN(target.getTime())) {
    return null;
  }

  let age = target.getFullYear() - birth.getFullYear();
  const monthDiff = target.getMonth() - birth.getMonth();

  if (
    monthDiff < 0 ||
    (monthDiff === 0 && target.getDate() < birth.getDate())
  ) {
    age -= 1;
  }

  return age;
};

const formatTodayLabel = () =>
  new Date().toLocaleDateString("fr-FR", {
    weekday: "long",
    day: "2-digit",
    month: "long",
    year: "numeric",
  });

const formatShortBirthdayDate = (date) => {
  if (!date) return "";

  return new Date(date).toLocaleDateString("fr-FR", {
    weekday: "short",
    day: "2-digit",
    month: "2-digit",
  });
};

const getBirthdayMessage = (adherent, mode) => {
  const firstName = getFirstName(adherent);

  if (mode === "today") {
    return `Nous souhaitons un joyeux anniversaire à ${firstName} 🎂`;
  }

  const nextBirthday = getNextBirthdayOccurrence(adherent?.date_naissance);
  const label = nextBirthday
    ? nextBirthday.toLocaleDateString("fr-FR", {
        day: "2-digit",
        month: "2-digit",
      })
    : "";

  return `Anniversaire à venir : ${firstName}${label ? ` le ${label}` : ""} 🎂`;
};

function SkeletonCard() {
  return (
    <div className="rounded-3xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-800/60">
      <div className="mx-auto h-16 w-16 animate-pulse rounded-full bg-slate-200 dark:bg-slate-700" />
      <div className="mx-auto mt-3 h-3 w-24 animate-pulse rounded-full bg-slate-200 dark:bg-slate-700" />
      <div className="mx-auto mt-2 h-3 w-16 animate-pulse rounded-full bg-slate-200 dark:bg-slate-700" />
      <div className="mx-auto mt-4 h-8 w-24 animate-pulse rounded-full bg-slate-200 dark:bg-slate-700" />
    </div>
  );
}

function BirthdayMiniCard({
  adherent,
  mode,
  hasToken,
  sending,
  onNotify,
  onOpen,
}) {
  const nextBirthday =
    mode === "next30"
      ? getNextBirthdayOccurrence(adherent?.date_naissance)
      : null;

  const days = mode === "next30" ? getDaysFromToday(nextBirthday) : 0;

  const age =
    mode === "next30"
      ? getAgeAtDate(adherent?.date_naissance, nextBirthday)
      : getAgeAtDate(adherent?.date_naissance, new Date());

  const img =
    adherent?.autorisation_photo === false
      ? "/img/interdiction.jpg"
      : toAbsolute(adherent?.photo_url) || getDefaultPhoto(adherent?.genre);

  return (
    <article
      className="group relative overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm transition hover:-translate-y-0.5 hover:shadow-md dark:border-slate-800 dark:bg-slate-900"
      role={onOpen ? "button" : "article"}
      tabIndex={onOpen ? 0 : undefined}
      onClick={() => onOpen?.(adherent)}
      onKeyDown={(e) => {
        if (e.key === "Enter") onOpen?.(adherent);
      }}
    >
      <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F]" />

      <div className="p-4">
        <div className="flex flex-col items-center text-center">
          <div className="relative">
            <img
              src={img}
              alt={getDisplayName(adherent)}
              className="h-20 w-20 rounded-full object-cover ring-4 ring-slate-100 transition group-hover:scale-[1.03] dark:ring-slate-800"
              onError={(e) => {
                e.currentTarget.src = getDefaultPhoto(adherent?.genre);
              }}
            />

            <span className="absolute -right-1 -top-1 grid h-8 w-8 place-items-center rounded-full bg-gradient-to-br from-amber-400 to-pink-500 text-white shadow-md ring-2 ring-white dark:ring-slate-900">
              <FaGift className="text-sm" />
            </span>
          </div>

          <h3 className="mt-3 max-w-full truncate text-sm font-bold text-slate-900 dark:text-white">
            {getDisplayName(adherent)}
          </h3>

          <div className="mt-1 flex flex-wrap justify-center gap-1.5">
            <span className="rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[11px] font-semibold text-slate-600 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300">
              {Number.isFinite(age) ? `${age} ans` : "Âge inconnu"}
            </span>

            {mode === "next30" && nextBirthday && (
              <span className="rounded-full border border-indigo-200 bg-indigo-50 px-2 py-0.5 text-[11px] font-semibold text-indigo-700 dark:border-indigo-500/30 dark:bg-indigo-500/10 dark:text-indigo-200">
                {formatShortBirthdayDate(nextBirthday)}
              </span>
            )}

            {mode === "next30" && Number.isFinite(days) && (
              <span className="rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-700 dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-200">
                {days === 0 ? "Aujourd’hui" : `J+${days}`}
              </span>
            )}
          </div>

          {Array.isArray(adherent?.Groups) && adherent.Groups.length > 0 && (
            <p className="mt-2 line-clamp-1 max-w-full text-[11px] text-slate-500 dark:text-slate-400">
              {adherent.Groups.map((g) => g?.nom).filter(Boolean).join(", ")}
            </p>
          )}

          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onNotify(adherent);
            }}
            disabled={sending || !hasToken}
            title={
              hasToken
                ? "Envoyer une notification"
                : "Vous devez être connecté pour notifier"
            }
            className={`mt-4 inline-flex h-9 items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-[#013E8E] via-indigo-600 to-violet-600 px-3 text-xs font-bold text-white shadow-sm transition hover:shadow-md ${
              sending || !hasToken
                ? "cursor-not-allowed opacity-60"
                : "hover:-translate-y-0.5"
            }`}
          >
            {sending ? (
              <>
                <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-white/40 border-t-white" />
                Envoi…
              </>
            ) : (
              <>
                <FaBell />
                Notifier
              </>
            )}
          </button>
        </div>
      </div>
    </article>
  );
}

export default function ListingAdherentsBirthdaysCard({
  adherents = [],
  birthdayAdherents,
  loading = false,
  initialExpanded = false,
  onOpenAdherent,
}) {
  const todayAdherents = useMemo(() => {
    if (Array.isArray(birthdayAdherents)) return birthdayAdherents;
    return Array.isArray(adherents) ? adherents : [];
  }, [adherents, birthdayAdherents]);

  const [expanded, setExpanded] = useState(initialExpanded);
  const [mode, setMode] = useState("today");

  const [upcomingAdherents, setUpcomingAdherents] = useState([]);
  const [loadingUpcoming, setLoadingUpcoming] = useState(false);

  const [sendingIds, setSendingIds] = useState(new Set());
  const [userIdsCache, setUserIdsCache] = useState({});

  const hasToken = useMemo(() => {
    try {
      return !!localStorage.getItem("token");
    } catch {
      return false;
    }
  }, []);

  const list = mode === "today" ? todayAdherents : upcomingAdherents;
  const isLoadingList = loading || (mode === "next30" && loadingUpcoming);
  const hasBirthdays = list.length > 0;

  const todayLabel = useMemo(() => formatTodayLabel(), []);

  const setSending = useCallback((id, value) => {
    setSendingIds((prev) => {
      const next = new Set(prev);

      if (value) next.add(id);
      else next.delete(id);

      return next;
    });
  }, []);

  const resolveUserIds = useCallback(
    async (adherentId) => {
      if (userIdsCache[adherentId]) {
        return userIdsCache[adherentId];
      }

      try {
        const users = await getUserIdByAdherentId(adherentId);
        const ids = (Array.isArray(users) ? users : [])
          .map(pickUserId)
          .filter(Boolean);

        const uniqueIds = Array.from(new Set(ids));

        setUserIdsCache((prev) => ({
          ...prev,
          [adherentId]: uniqueIds,
        }));

        return uniqueIds;
      } catch (error) {
        console.error("Erreur récupération user ids :", error);
        return [];
      }
    },
    [userIdsCache]
  );

  const fetchUpcomingBirthdays = useCallback(async () => {
    try {
      setLoadingUpcoming(true);

      const data = await getUpcomingBirthdays();
      setUpcomingAdherents(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Erreur anniversaires à venir :", error);
      setUpcomingAdherents([]);
      toast.error("Erreur lors du chargement des anniversaires à venir.");
    } finally {
      setLoadingUpcoming(false);
    }
  }, []);

  useEffect(() => {
    if (mode !== "next30") return;
    if (upcomingAdherents.length > 0) return;

    fetchUpcomingBirthdays();
  }, [mode, upcomingAdherents.length, fetchUpcomingBirthdays]);

  const handleModeChange = (nextMode) => {
    setMode(nextMode);
    setExpanded(true);
  };

  const handleRefreshUpcoming = async () => {
    setUpcomingAdherents([]);
    await fetchUpcomingBirthdays();
  };

  const handleSendOne = async (adherent) => {
    if (!hasToken) {
      toast.error("Vous devez être connecté pour envoyer la notification.");
      return;
    }

    const adherentId = adherent?.id;
    if (!adherentId) return;

    setSending(adherentId, true);

    try {
      const userIds = await resolveUserIds(adherentId);

      if (!userIds.length) {
        toast.info("Aucun destinataire lié à cet adhérent.");
        return;
      }

      const message = getBirthdayMessage(adherent, mode);

      const results = await Promise.allSettled(
        userIds.map((userId) => sendNotification(message, userId))
      );

      const success = results.filter((r) => r.status === "fulfilled").length;
      const failed = results.length - success;

      if (success && !failed) {
        toast.success("Notification envoyée.");
      } else if (success && failed) {
        toast.warn(`Envoi partiel : ${success} envoyée(s), ${failed} échec(s).`);
      } else {
        toast.error("Échec de l’envoi de la notification.");
      }
    } catch (error) {
      console.error("Erreur notification anniversaire :", error);
      toast.error("Erreur lors de l’envoi de la notification.");
    } finally {
      setSending(adherentId, false);
    }
  };

  return (
    <section className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
      <div className="h-1.5 bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F]" />

      <div className="p-4 sm:p-5 lg:p-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex items-start gap-3">
            <div className="grid h-12 w-12 shrink-0 place-items-center rounded-2xl bg-gradient-to-br from-amber-400 via-pink-500 to-violet-600 text-white shadow-sm">
              <FaBirthdayCake className="text-xl" />
            </div>

            <div>
              <p className="text-[11px] uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">
                Anniversaires
              </p>

              <h2 className="mt-1 text-xl font-bold text-slate-900 dark:text-white">
                {mode === "today"
                  ? "Anniversaires du jour"
                  : "Prochains anniversaires"}
              </h2>

              <p className="mt-1 text-sm leading-6 text-slate-600 dark:text-slate-300">
                {mode === "today"
                  ? todayLabel
                  : "Adhérents ayant leur anniversaire dans les 30 prochains jours."}
              </p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <div className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-800/60">
              <FaUserClock className="text-slate-400" />
              <span className="text-slate-500 dark:text-slate-400">
                Total :
              </span>
              <span className="font-bold text-slate-900 dark:text-white">
                {isLoadingList ? "…" : list.length}
              </span>
            </div>

            <div className="inline-flex rounded-2xl border border-slate-200 bg-slate-50 p-1 dark:border-slate-700 dark:bg-slate-800/60">
              <button
                type="button"
                onClick={() => handleModeChange("today")}
                className={`rounded-xl px-3 py-1.5 text-xs font-bold transition ${
                  mode === "today"
                    ? "bg-[#013E8E] text-white shadow-sm"
                    : "text-slate-600 hover:bg-white dark:text-slate-300 dark:hover:bg-slate-700"
                }`}
              >
                Aujourd’hui
              </button>

              <button
                type="button"
                onClick={() => handleModeChange("next30")}
                className={`rounded-xl px-3 py-1.5 text-xs font-bold transition ${
                  mode === "next30"
                    ? "bg-[#013E8E] text-white shadow-sm"
                    : "text-slate-600 hover:bg-white dark:text-slate-300 dark:hover:bg-slate-700"
                }`}
              >
                30 jours
              </button>
            </div>

            {mode === "next30" && (
              <button
                type="button"
                onClick={handleRefreshUpcoming}
                disabled={loadingUpcoming}
                className="inline-flex h-10 items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-3 text-xs font-bold text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
                title="Rafraîchir les anniversaires à venir"
              >
                <FaSyncAlt className={loadingUpcoming ? "animate-spin" : ""} />
                <span className="hidden sm:inline">Rafraîchir</span>
              </button>
            )}

            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              className="inline-flex h-10 items-center justify-center gap-2 rounded-2xl bg-slate-900 px-3 text-xs font-bold text-white shadow-sm transition hover:bg-slate-800 dark:bg-white dark:text-slate-900 dark:hover:bg-slate-100"
              aria-expanded={expanded}
            >
              {expanded ? (
                <>
                  <FaChevronUp />
                  Replier
                </>
              ) : (
                <>
                  <FaChevronDown />
                  Déployer
                </>
              )}
            </button>
          </div>
        </div>

        {!expanded && hasBirthdays && !isLoadingList && (
          <div className="mt-5 flex items-center gap-3 overflow-x-auto pb-1">
            {list.slice(0, 8).map((adherent) => {
              const img =
                adherent?.autorisation_photo === false
                  ? "/img/interdiction.jpg"
                  : toAbsolute(adherent?.photo_url) ||
                    getDefaultPhoto(adherent?.genre);

              return (
                <button
                  key={adherent.id}
                  type="button"
                  onClick={() => onOpenAdherent?.(adherent)}
                  className="group shrink-0"
                  title={getDisplayName(adherent)}
                >
                  <img
                    src={img}
                    alt={getDisplayName(adherent)}
                    className="h-12 w-12 rounded-full object-cover ring-2 ring-slate-100 transition group-hover:scale-105 dark:ring-slate-800"
                    onError={(e) => {
                      e.currentTarget.src = getDefaultPhoto(adherent?.genre);
                    }}
                  />
                </button>
              );
            })}

            {list.length > 8 && (
              <div className="grid h-12 w-12 shrink-0 place-items-center rounded-full bg-slate-100 text-xs font-bold text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                +{list.length - 8}
              </div>
            )}
          </div>
        )}

        {expanded && (
          <div className="mt-5">
            {isLoadingList ? (
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6">
                {[...Array(6)].map((_, index) => (
                  <SkeletonCard key={index} />
                ))}
              </div>
            ) : !hasBirthdays ? (
              <div className="rounded-3xl border border-dashed border-slate-300 bg-slate-50 p-6 text-center dark:border-slate-700 dark:bg-slate-800/40">
                <div className="mx-auto grid h-12 w-12 place-items-center rounded-2xl bg-white text-slate-400 shadow-sm dark:bg-slate-900">
                  <FaCalendarAlt />
                </div>

                <h3 className="mt-3 text-base font-bold text-slate-900 dark:text-white">
                  Aucun anniversaire
                </h3>

                <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                  {mode === "today"
                    ? "Aucun adhérent ne fête son anniversaire aujourd’hui."
                    : "Aucun anniversaire n’est prévu dans les 30 prochains jours."}
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6">
                {list.map((adherent) => (
                  <BirthdayMiniCard
                    key={adherent.id}
                    adherent={adherent}
                    mode={mode}
                    hasToken={hasToken}
                    sending={sendingIds.has(adherent.id)}
                    onNotify={handleSendOne}
                    onOpen={onOpenAdherent}
                  />
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
}